import { useCallback, useEffect, useState } from "react";
import { getQuranClass } from "../services/liveQuranService";
import type { PreviousClass } from "../types/liveQuran";

export interface QuranClassState {
  isLoading: boolean;
  checkClass: boolean;
  currentClass: string;
  previousClass: PreviousClass[];
  /** Switch the player to a previously recorded class. */
  playPreviousVideo: (url: string) => void;
}

/**
 * Loads the current/previous Quran classes for the given token and exposes the
 * player state.
 *
 * The request is guarded with an `active` flag so a response that resolves after
 * the component unmounts (or after the token changes) cannot set state on a
 * stale render — the original implementation had no such protection.
 */
export const useQuranClass = (token: string | null): QuranClassState => {
  const [isLoading, setIsLoading] = useState(true);
  const [checkClass, setCheckClass] = useState(false);
  const [currentClass, setCurrentClass] = useState("");
  const [previousClass, setPreviousClass] = useState<PreviousClass[]>([]);

  useEffect(() => {
    let active = true;

    const loadClass = async (): Promise<void> => {
      try {
        const { status, response } = await getQuranClass();
        if (!active || status !== 200) {
          return;
        }

        if (response.live !== null) {
          setCurrentClass(response.live.url);
          setCheckClass(true);
        } else {
          setCheckClass(false);
        }

        if (response.previous.length > 0) {
          setPreviousClass(response.previous);
        }

        setIsLoading(false);
      } catch (error) {
        if (!active) {
          return;
        }
        console.log("getClass", error);
        setIsLoading(false);
      }
    };

    void loadClass();

    return () => {
      active = false;
    };
  }, [token]);

  const playPreviousVideo = useCallback((url: string) => {
    setCurrentClass(url);
    setCheckClass(true);
  }, []);

  return { isLoading, checkClass, currentClass, previousClass, playPreviousVideo };
};
